import { useCallback, useEffect, useState, type ReactNode } from 'react';
import { apiRequest } from '../../api/client.js';
import { useAuth } from '../../auth/AuthProvider.js';
import { useTranslation } from '../../../../shared/i18n/index.js';
import { useBusinessDay } from '../../../../shared/hooks/useBusinessDay.js';

/**
 * M07 L7.04 — Branding-officer clarification thread list.
 *
 * Tenant-wide register of clarifications raised to vendors, showing the
 * question and (when present) the vendor's reply. Reads the add-only backend
 * endpoint `GET /v1/vendor/clarifications`.
 */
interface ClarificationRow {
  readonly clarificationId: string;
  readonly jobRequestId: string;
  readonly vendorAssignmentId: string;
  readonly vendorId: string;
  readonly subject: string;
  readonly questionText: string;
  readonly raisedBy: string;
  readonly raisedAt: string;
  readonly repliedBy: string | null;
  readonly repliedAt: string | null;
  readonly replyText: string | null;
  readonly status: 'OPEN' | 'ANSWERED';
}

interface ThreadResponse {
  readonly rows: ClarificationRow[];
  readonly total: number;
  readonly limit: number;
  readonly offset: number;
}

const STATUSES: Array<'' | 'OPEN' | 'ANSWERED'> = ['', 'OPEN', 'ANSWERED'];

export function BoClarificationsPage(): ReactNode {
  const { t } = useTranslation();
  // Dates follow the tenant's DISPLAY_DATE_FORMAT, not the reader's language picker.
  const { atDay } = useBusinessDay();
  const { token } = useAuth();
  const [data, setData] = useState<ThreadResponse | null>(null);
  const [error, setError] = useState<string | null>(null);
  const [status, setStatus] = useState<'' | 'OPEN' | 'ANSWERED'>('');

  const load = useCallback(async () => {
    setData(null);
    setError(null);
    try {
      const params = new URLSearchParams();
      if (status) params.set('status', status);
      params.set('limit', '50');
      const res = await apiRequest<ThreadResponse>(`/v1/vendor/clarifications?${params.toString()}`, { token });
      setData(res);
    } catch (e) {
      setError((e as Error).message);
      setData({ rows: [], total: 0, limit: 50, offset: 0 });
    }
  }, [token, status]);

  useEffect(() => { void load(); }, [load]);

  return (
    <main aria-label="bo-clarifications">
      <h1 style={{ fontSize: 20, marginBottom: 4 }}>{t('bo.clarifications.title')}</h1>
      <p style={{ color: 'var(--color-text-muted)', marginBottom: 16, fontSize: 13 }}>
        {t('bo.clarifications.intro')}
      </p>

      <div style={{ display: 'flex', gap: 8, alignItems: 'center', marginBottom: 16 }}>
        <label htmlFor="status-filter" style={{ fontSize: 12 }}>{t('bo.clarifications.status')}</label>
        <select id="status-filter" aria-label="status-filter" value={status}
                onChange={(e) => setStatus(e.target.value as '' | 'OPEN' | 'ANSWERED')}
                style={{ padding: '5px 8px', borderRadius: 4, border: '1px solid var(--color-border-default)' }}>
          {STATUSES.map((s) => <option key={s || 'all'} value={s}>{s || t('bo.clarifications.all')}</option>)}
        </select>
      </div>

      {error && <p role="alert" style={{ color: 'var(--color-danger)' }}>{error}</p>}
      {!data ? (
        <p>{t('bo.clarifications.loading')}</p>
      ) : data.rows.length === 0 ? (
        <p data-testid="clarifications-empty" style={{ color: 'var(--color-text-muted)' }}>{t('bo.clarifications.empty')}</p>
      ) : (
        <ul aria-label="clarifications-list" style={{ listStyle: 'none', padding: 0, display: 'flex', flexDirection: 'column', gap: 12 }}>
          {data.rows.map((c) => (
            <li key={c.clarificationId} data-testid={`clarification-${c.clarificationId}`}
                style={{ border: '1px solid var(--color-border-default)', borderRadius: 6, padding: 12 }}>
              <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', marginBottom: 6 }}>
                <strong>{c.subject}</strong>
                <span data-testid={`status-${c.clarificationId}`}
                      style={{ fontSize: 12, padding: '2px 8px', borderRadius: 10,
                               background: c.status === 'OPEN' ? 'var(--color-warning-bg)' : 'var(--color-success-bg)',
                               color: c.status === 'OPEN' ? 'var(--color-warning)' : 'var(--color-success)' }}>
                  {c.status}
                </span>
              </div>
              <p style={{ margin: '4px 0' }}><span style={{ color: 'var(--color-text-muted)' }}>{t('bo.clarifications.q')}</span> {c.questionText}</p>
              {c.replyText && (
                <p style={{ margin: '4px 0' }}><span style={{ color: 'var(--color-text-muted)' }}>{t('bo.clarifications.a')}</span> {c.replyText}</p>
              )}
              <div style={{ fontSize: 12, color: 'var(--color-text-muted)', marginTop: 6 }}>
                {t('bo.clarifications.raised')} {atDay(c.raisedAt)}
                {c.repliedAt && ` · ${t('bo.clarifications.replied')} ${atDay(c.repliedAt)}`}
              </div>
            </li>
          ))}
        </ul>
      )}
    </main>
  );
}
